import { useCallback, useMemo } from "react";

import { useAudienceActions } from "@/context";
import { Site, SiteAudience } from "@/types";
import { ALL_SITES, changeSites, isAllSites, toggleItem, toggleSite } from "@/utils";
import { AudienceWorkflow } from "./useAudienceWorkflow";

const NO_IDS: number[] = [];
const NO_CATEGORIES: string[] = [];

/**
 * Site, category and day type selection for a site audience. Changing the
 * sites reloads the catchment; changing a filter re-sizes the loaded one.
 */
export const useSiteSelection = (
  audience: SiteAudience | null | undefined,
  workflow: Pick<AudienceWorkflow, "loadCatchment" | "buildSite">
) => {
  const { setSites, setSiteCategories, setSiteDayType } = useAudienceActions();
  const { loadCatchment, buildSite } = workflow;

  const siteIds = audience?.site_config.sites ?? NO_IDS;
  const categories = audience?.site_config.categories ?? NO_CATEGORIES;
  const dayType = audience?.site_config.day_type || 'ALL';

  const updateSites = useCallback((updated: number[]) => {
    setSites(updated);
    loadCatchment(updated, categories, dayType);
  }, [categories, dayType, loadCatchment, setSites]);

  const selectAll = useCallback(() => updateSites(ALL_SITES), [updateSites]);

  const toggle = useCallback((site: Site) => updateSites(toggleSite(siteIds, site.site_id)), [siteIds, updateSites]);

  /** Selects every site in a region, or removes them all */
  const selectRegion = useCallback((sites: Site[], remove: boolean) =>
    updateSites(changeSites(siteIds, sites.map((s) => s.site_id), remove)), [siteIds, updateSites]);

  const remove = useCallback((site: Site) =>
    updateSites(siteIds.filter((id) => id !== site.site_id)), [siteIds, updateSites]);

  const clear = useCallback(() => {
    setSites([]);
    loadCatchment([], [], 'ALL');
  }, [loadCatchment, setSites]);

  const toggleCategory = useCallback((category: string) => {
    const updated = toggleItem(categories, category);
    setSiteCategories(updated);
    buildSite(updated, dayType);
  }, [buildSite, categories, dayType, setSiteCategories]);

  const selectDayType = useCallback((updated: string) => {
    setSiteDayType(updated);
    buildSite(categories, updated);
  }, [buildSite, categories, setSiteDayType]);

  return useMemo(() => ({
    allSelected: isAllSites(siteIds),
    selectAll, toggle, selectRegion, remove, clear, toggleCategory, selectDayType,
  }), [siteIds, selectAll, toggle, selectRegion, remove, clear, toggleCategory, selectDayType]);
};
